/**
 * The transcript.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE TWO ROLES LOOK NOTHING ALIKE
 *
 * The user's message is a bubble; the agent's is not. That asymmetry is what
 * every chat product converged on, and the reason is legibility rather than
 * fashion: a question is short and benefits from being visibly bounded, while
 * an answer can run to a table of six invoices and a bulleted summary. Boxing
 * that in a tinted bubble makes long-form content harder to read and wastes a
 * quarter of the width on padding.
 *
 * So the agent's reply sits directly on the surface, rendered as markdown, with
 * its trace above it and what it cost below it.
 */

import { DocumentCard } from './DocumentCard';
import { useEffect, useState, useRef } from 'react';
import { Coins, Paperclip, Sparkles } from 'lucide-react';
import { Button } from '@/components/shadcn/button';
import { useI18n } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import { Markdown } from './Markdown';
import { Logo } from '@/components/ui/Logo';
import { Trace } from './Trace';
import { Thinking } from './Thinking';
import type { TraceStep, AgentMessage, PendingChange } from './types';
import { TopUpDialog } from '@/components/billing/TopUpDialog';

/** `{n}` → value. The panel's strings are few; a whole i18n helper is overkill. */
const fill = (str: string, vars: Record<string, string | number>): string =>
  Object.entries(vars).reduce((out, [k, v]) => out.replaceAll(`{${k}}`, String(v)), str);

export function MessageList({
  messages,
  decided,
  onDecide,
  error,
  className,
  wide,
}: {
  messages: AgentMessage[];
  decided: Record<string, 'applied' | 'discarded' | 'busy' | 'gone'>;
  onDecide: (id: string, action: 'apply' | 'discard') => void;
  error: string | null;
  className?: string;
  /** Full-page mode centres the column; the rail uses its full width. */
  wide?: boolean;
}) {
  const [topUpOpen, setTopUpOpen] = useState(false);
  const { lang, t } = useI18n();
  const locale = lang === 'ar' ? 'ar-BH-u-nu-latn' : 'en';
  const endRef = useRef<HTMLDivElement>(null);

  /*
    Follow the conversation down as it grows — including on every token of a
    streaming reply, which is why the effect depends on `messages` rather than
    on its length. `block: 'end'` keeps the composer in view instead of
    scrolling it off the bottom.
  */
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  return (
    <div className={cn('mx-auto w-full space-y-5', wide && 'max-w-3xl', className)}>
      {messages.map((m) =>
        m.role === 'user' ? (
          <div key={m.id} className="flex flex-col items-end gap-1">
            {m.text ? (
              <div
                dir="auto"
                className="max-w-[85%] rounded-2xl bg-muted px-3.5 py-2 text-start text-[13.5px] leading-relaxed whitespace-pre-wrap"
              >
                {m.text}
              </div>
            ) : null}
            {m.files?.length ? (
              <div className="flex flex-wrap justify-end gap-1">
                {m.files.map((name) => (
                  <span
                    key={name}
                    dir="auto"
                    className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10.5px] text-muted-foreground"
                  >
                    <Paperclip className="size-2.5" aria-hidden />
                    {name}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div key={m.id} className="flex flex-col gap-1">
            {/* Reasoning first, then the work and the words interleaved in the
                order they actually happened. */}
            <Thinking text={m.thinking ?? ''} live={Boolean(m.streaming)} />

            {interleave(m.text ?? '', m.trace ?? []).map((seg, i) =>
              seg.kind === 'text' ? (
                <Markdown key={`t${i}`}>{seg.text}</Markdown>
              ) : (
                <Trace key={`s${i}`} steps={seg.steps} live={Boolean(m.streaming)} />
              ),
            )}

            {!m.text && m.streaming ? (
              /*
                The mark, running, rather than a pulsing bar.

                A generic dot says "something is happening somewhere". The mark
                with its sleepers moving says the same thing in the product's
                own voice, in the place the answer is about to appear — and it
                is already on screen in the sidebar, so it costs the reader no
                new vocabulary. Stops under reduced-motion like every other
                moving thing here.
              */
              <Logo size={22} markOnly animate className="opacity-60" />
            ) : null}

            {m.unread?.length ? (
              <p dir="auto" className="text-[10.5px] text-amber-600 dark:text-amber-500">
                {m.unread.join('، ')} — <UnreadLabel />
              </p>
            ) : null}

            {/* Documents before proposals: a PDF is the answer, a staged
                change is a question, and the answer should be read first. */}
            {m.documents?.length ? (
              <div className="flex flex-wrap gap-2 pt-1">
                {m.documents.map((d) => (
                  <DocumentCard key={`${d.entity_type}-${d.id}`} doc={d} />
                ))}
              </div>
            ) : null}

            {m.pending?.map((p) => (
              <ProposedChange
                key={p.id}
                change={p}
                state={decided[p.id]}
                onDecide={onDecide}
              />
            ))}

            {m.credits !== undefined && m.credits > 0 && !m.streaming ? (
              <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10.5px] tabular-nums text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Coins className="size-2.5" aria-hidden />
                  <TurnCost n={m.credits.toLocaleString(locale)} />
                </span>
                {/*
                  Which tier answered, and why, beside what it cost.

                  Auto spends the customer's credits on their behalf, so it owes
                  them the reason in the same breath as the number — otherwise a
                  347-credit turn next to a 12-credit one looks arbitrary rather
                  than like the deliberate choice it was.
                */}
                {m.tier ? (
                  <span className="text-muted-foreground/80">
                    · {m.tier.name}
                    {m.tier.reason ? ` — ${m.tier.reason}` : ''}
                  </span>
                ) : null}
              </span>
            ) : null}
          </div>
        ),
      )}

      {error === 'NO_CREDITS' ? (
        /*
          The credits ran out mid-conversation, which is exactly where the
          customer is most willing to fix it and least willing to go hunting.
          So the offer is here, in the thread, one button from done.
        */
        <div className="rounded-xl border border-border bg-muted/40 p-4">
          <p className="text-[13.5px] font-medium text-foreground">{t('credits_out_chat_title')}</p>
          <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
            {t('credits_out_chat_body')}
          </p>
          <Button size="sm" className="mt-3" onClick={() => setTopUpOpen(true)}>
            <Sparkles className="size-3.5" aria-hidden />
            {t('credits_out_chat_cta')}
          </Button>
        </div>
      ) : error ? (
        <p className="rounded-lg bg-destructive/10 px-3 py-2 text-[12.5px] text-destructive">
          {error}
        </p>
      ) : null}
      <TopUpDialog open={topUpOpen} onClose={() => setTopUpOpen(false)} />
      <div ref={endRef} />
    </div>
  );
}

function UnreadLabel() {
  const { t } = useI18n();
  return <>{t('agent_file_unread')}</>;
}

function TurnCost({ n }: { n: string }) {
  const { t } = useI18n();
  return <>{fill(t('agent_turn_cost'), { n })}</>;
}

/**
 * A proposed change, with the only button that can commit it.
 *
 * The card says in as many words that nothing is saved yet, because the reply
 * above it was written by a model — and a model that says "I've created the
 * invoice" is the exact failure this card exists to prevent.
 */
function ProposedChange({
  change,
  state,
  onDecide,
}: {
  change: PendingChange;
  state?: 'applied' | 'discarded' | 'busy' | 'gone';
  onDecide: (id: string, action: 'apply' | 'discard') => void;
}) {
  const { t } = useI18n();
  /*
    'gone' settles the card too, and that is the bug this fixes.

    Pressing Apply on a proposal the server had already superseded threw a 409,
    the panel deleted its local state, and the buttons came straight back. So
    the press did nothing, looked like it did nothing, and the only feedback was
    the words "status SUPERSEDED" in the error line. With several proposals on
    screen at once, which is the normal case after uploading a few statements,
    every stale one still offered a button that could not work.
  */
  const settled = state === 'applied' || state === 'discarded' || state === 'gone';

  /* Read out of the preview rather than passed as its own field: the preview is
     authored server-side and is the one thing guaranteed to describe what will
     actually be applied. A separate logo prop could disagree with it. */
  const logoUrl = /Logo:\s*(https:\/\/\S+)/.exec(change.preview)?.[1] ?? null;

  return (
    <div className="my-1 max-w-md rounded-xl border bg-card p-3">
      <p className="text-[11px] font-medium text-muted-foreground">{t('agent_proposed')}</p>
      <pre dir="auto" className="mt-1 font-sans text-[12.5px] leading-relaxed whitespace-pre-wrap">
        {change.preview}
      </pre>

      {/*
        A LOGO IS A PICTURE, AND THE CARD WAS SHOWING ITS URL.

        The company-profile proposal carries "Logo: https://…/icon.png", and
        approving a logo you cannot see is not approving anything — the one
        field on that card whose correctness is obvious at a glance was the one
        rendered as text to read.

        Deliberately forgiving: this is a preview, so a broken or slow image
        hides itself and the line above still says what the URL is. onError
        rather than a load check, because the alternative is a grey box that
        reads as "no logo found" when it means "the image is still loading".
      */}
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
          className="mt-2 max-h-16 max-w-[160px] rounded-md border bg-white object-contain p-1.5"
        />
      ) : null}

      {settled ? (
        <p dir="auto" className="mt-2 text-[11.5px] text-muted-foreground">
          {state === 'gone'
            ? (change.settled_note ?? t('agent_no_longer_waiting'))
            : state === 'applied'
              ? t('agent_applied')
              : t('agent_discarded')}
        </p>
      ) : (
        <>
          {/*
            THE CARD SAYS WHAT IT REPEATS, AND THE BUTTON CHANGES ITS WORD.

            An entry identical to one posted in the last day is stopped and this
            card comes back carrying the reason. The sentence is the server's,
            because it holds the facts a person needs to decide: the earlier
            entry's number, how long ago it was posted and what its memo said.

            "Post it anyway" rather than "Apply", because the second press means
            something different from the first and a button that reads the same
            both times is asking somebody to remember which press they are on.
          */}
          {change.duplicate_warning ? (
            <p
              dir="auto"
              className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11.5px] leading-relaxed"
            >
              {change.duplicate_warning}
            </p>
          ) : null}
          <p className="mt-1.5 text-[10.5px] text-muted-foreground">{t('agent_not_applied')}</p>
          <div className="mt-2.5 flex gap-1.5">
            <Button size="sm" disabled={state === 'busy'} onClick={() => onDecide(change.id, 'apply')}>
              {change.duplicate_warning ? t('agent_apply_anyway') : t('agent_apply')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={state === 'busy'}
              onClick={() => onDecide(change.id, 'discard')}
            >
              {t('agent_discard')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Cut the reply where the agent stopped to do something, and put the doing there.
 *
 * The trace was one block above the whole answer, so a reply that ran
 * "I'll fetch that for you." → [looks something up] → "Here it is." showed
 * both lookups at the top and then a finished-looking sentence with nothing
 * under it. The agent was working; the screen said it had stopped.
 *
 * `afterChars` records how much text existed when each step began, so the
 * segments come out in the order they happened. Steps from history have no
 * such marker — those all sort to the front, which is where a single opening
 * block belongs and is exactly how it used to render.
 */
type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'steps'; steps: TraceStep[] };

function interleave(text: string, steps: TraceStep[]): Segment[] {
  if (steps.length === 0) return text ? [{ kind: 'text', text }] : [];

  /* Group by the cut they belong to, so consecutive lookups stay one block. */
  const byCut = new Map<number, TraceStep[]>();
  for (const s of steps) {
    const at = Math.min(s.afterChars ?? 0, text.length);
    byCut.set(at, [...(byCut.get(at) ?? []), s]);
  }

  const out: Segment[] = [];
  let cursor = 0;
  for (const at of [...byCut.keys()].sort((a, b) => a - b)) {
    if (at > cursor) {
      out.push({ kind: 'text', text: text.slice(cursor, at) });
      cursor = at;
    }
    out.push({ kind: 'steps', steps: byCut.get(at)! });
  }
  if (cursor < text.length) out.push({ kind: 'text', text: text.slice(cursor) });
  return out;
}
